// Service-worker update prompt. The PWA registers with
// registerType: "prompt" (see vite.config.ts), so a freshly deployed
// build does NOT swap under the user mid-session: the new worker waits.
// When it is applied without asking, and when never, is updatePolicy.ts
// (in short: in the background, or on a launch or return before any tap,
// and never while a reload would lose something in ANY open tab,
// updateHold.ts); otherwise this glass toast offers "Update".
//
// This component also finds a new build itself: it asks on every return
// to the foreground (at most once a minute; a resumed PWA is not a
// navigation, so the browser does not) and hourly, and it watches the
// registration directly rather than only through workbox-window's events
// (workbox misses a build that starts installing before it registers,
// after window load, and stops listening after one "external" update).
// Applying = SKIP_WAITING to the waiting worker, which switches every open
// tab to the new build. Each tab then decides its own reload (the
// controllerchange listener below, not the plugin's reload-everything): the
// tab that applied reloads; another reloads at once unless it holds (a page
// left on the old build under the new worker loses its lazy routes:
// vite.config.ts), and one that holds keeps its page, shows the toast and
// reloads at its next safe moment (updatePolicy.ts).
//
// Mounted once at the root (main.tsx). Renders nothing until an update is
// actually waiting.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Check, RefreshCw, X } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { colors, glassPanel, glassButtonPrimary, radius } from "@/theme";

import { HOLD_REFRESH_MS, heldHere, shareHold, updateHeld } from "./updateHold";
import { mayApplyUpdate, shouldCheckForUpdate } from "./updatePolicy";
import { recordingActive, subscribeRecordingActive, updateButton } from "@/features/meetings/recorder/recordingHold";

const UPDATE_POLL_MS = 60 * 60 * 1000; // hourly

/**
 * A build that waits behind the running one. A first install passes
 * through "waiting" for a moment on its way to active, with nothing active
 * before it: that is not an update (it showed the toast on a new device).
 */
function waitsBehind(registration: ServiceWorkerRegistration, sw: ServiceWorker): boolean {
  return registration.waiting === sw && registration.active !== null && registration.active !== sw;
}

/** Call `onWaiting` whenever this registration has a new build waiting. */
function watchForWaiting(registration: ServiceWorkerRegistration, onWaiting: () => void) {
  const track = (sw: ServiceWorker | null) => {
    if (!sw) return;
    const onState = () => {
      if (sw.state !== "installed") return;
      // Look again a moment later, as workbox-window does: a worker that is
      // only passing through "installed" has moved on by then.
      window.setTimeout(() => {
        if (sw.state === "installed" && waitsBehind(registration, sw)) onWaiting();
      }, 200);
    };
    sw.addEventListener("statechange", onState);
    onState();
  };
  const waiting = registration.waiting;
  if (waiting && waitsBehind(registration, waiting)) onWaiting();
  track(registration.installing);
  registration.addEventListener("updatefound", () => track(registration.installing));
}

let applying = false;

// "Updated" after the reload, so an update is never silent: every reload into a new build marks
// this tab (sessionStorage survives the reload), and the page that loads reads it once.
const JUST_UPDATED_KEY = "lattice.pwa.justUpdated";
// If the new build has not taken over this long after Update, reload anyway: a home-screen app
// on iOS does not always deliver controllerchange, and the button must never just sit there.
const TAKEOVER_FALLBACK_MS = 3_000;

function reloadIntoUpdate() {
  try {
    sessionStorage.setItem(JUST_UPDATED_KEY, "1");
  } catch {
    // Private mode or blocked storage: the reload still happens, only the note is lost.
  }
  window.location.reload();
}

const JUST_UPDATED = (() => {
  try {
    const marked = sessionStorage.getItem(JUST_UPDATED_KEY) === "1";
    sessionStorage.removeItem(JUST_UPDATED_KEY);
    return marked;
  } catch {
    return false;
  }
})();

export function PwaUpdatePrompt() {
  const registration = useRef<ServiceWorkerRegistration | null>(null);
  const lastCheck = useRef(Date.now());
  const shownAt = useRef(Date.now());
  const interacted = useRef(false);
  // A build took over while this tab held: it runs the old build under the
  // new worker until it can reload.
  const reloadDue = useRef(false);

  const check = useCallback(() => {
    const reg = registration.current;
    if (!reg) return;
    lastCheck.current = Date.now();
    reg.update().catch(() => undefined); // offline, or mid-deploy: the next return tries again
  }, []);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
  } = useRegisterSW({
    onRegisteredSW(_swUrl, reg) {
      if (!reg) return;
      registration.current = reg;
      watchForWaiting(reg, () => setNeedRefresh(true));
      setInterval(check, UPDATE_POLL_MS);
    },
    // The plugin would reload every tab that saw the build waiting, a held
    // one too; the controllerchange listener below decides instead.
    onNeedReload() {},
  });

  // Update was pressed (or applied on its own): the button says so until the page reloads.
  const [updating, setUpdating] = useState(false);
  // A reload stops a meeting recording in this page: the toast waits for it (nothing to press until then).
  const recording = useSyncExternalStore(subscribeRecordingActive, recordingActive, recordingActive);
  const button = updateButton(recording, updating);

  /** Update: the reload this tab put off, else activate the waiting build. */
  const apply = useCallback(() => {
    setUpdating(true);
    if (reloadDue.current) {
      reloadIntoUpdate();
      return;
    }
    const reg = registration.current;
    const sw = reg?.waiting ?? null;
    if (!reg || !sw || !waitsBehind(reg, sw)) {
      // Nothing waits any more: another tab most likely applied it already, so the new build is
      // the active one and a reload loads it (it used to hide the toast and stay on the old page).
      reloadIntoUpdate();
      return;
    }
    if (applying) return;
    applying = true;
    sw.postMessage({ type: "SKIP_WAITING" });
    window.setTimeout(reloadIntoUpdate, TAKEOVER_FALLBACK_MS);
  }, []);

  // Read by the listeners below, which are added once.
  const waiting = useRef(needRefresh);
  waiting.current = needRefresh;
  const applyIfSafe = useRef(() => {});
  applyIfSafe.current = () => {
    if (!waiting.current && !reloadDue.current) return;
    const moment = {
      hidden: document.visibilityState === "hidden",
      shownAt: shownAt.current,
      interacted: interacted.current,
      // A put-off reload waits for this tab only; applying a build, for every tab.
      held: reloadDue.current ? heldHere() : updateHeld(),
      now: Date.now(),
    };
    if (mayApplyUpdate(moment)) apply();
  };

  // A new build controls this page now: reload into it, unless this tab
  // holds and did not ask for it. A page no build controlled at load is
  // never taken over (clientsClaim: false, vite.config.ts).
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw?.controller) return;
    const onTakeover = () => {
      if (applying || !heldHere()) {
        reloadIntoUpdate();
        return;
      }
      reloadDue.current = true;
      setNeedRefresh(true);
    };
    sw.addEventListener("controllerchange", onTakeover);
    return () => sw.removeEventListener("controllerchange", onTakeover);
  }, [setNeedRefresh]);

  // A launch or a return to the foreground starts a fresh moment (no tap,
  // key or scroll yet) and asks for a new build; any trip to the
  // background or back applies a waiting one if nothing would be lost. The
  // other tabs hear whether this one holds (updateHold.ts).
  useEffect(() => {
    const touched = () => {
      interacted.current = true;
    };
    const share = () => shareHold();
    const onVisibility = () => {
      shareHold();
      if (document.visibilityState === "visible") {
        shownAt.current = Date.now();
        interacted.current = false;
        if (!waiting.current && shouldCheckForUpdate(lastCheck.current, Date.now())) check();
      }
      applyIfSafe.current();
    };
    const onPageHide = () => {
      applyIfSafe.current();
      shareHold(true);
    };
    const passive = { capture: true, passive: true };
    window.addEventListener("pointerdown", touched, passive);
    window.addEventListener("wheel", touched, passive);
    window.addEventListener("keydown", touched, { capture: true });
    window.addEventListener("input", share, { capture: true });
    window.addEventListener("focusin", share);
    window.addEventListener("focusout", share);
    window.addEventListener("pageshow", share);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    const refresh = window.setInterval(share, HOLD_REFRESH_MS);
    shareHold();
    return () => {
      window.removeEventListener("pointerdown", touched, { capture: true });
      window.removeEventListener("wheel", touched, { capture: true });
      window.removeEventListener("keydown", touched, { capture: true });
      window.removeEventListener("input", share, { capture: true });
      window.removeEventListener("focusin", share);
      window.removeEventListener("focusout", share);
      window.removeEventListener("pageshow", share);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.clearInterval(refresh);
      shareHold(true);
    };
  }, [check]);

  // The moment a build is waiting: now, if nothing is lost; otherwise the
  // toast, and the listeners above apply it later.
  useEffect(() => {
    if (needRefresh) applyIfSafe.current();
  }, [needRefresh]);

  // "Not now" hides the toast for this page; the build is still applied on
  // the next trip away or back once nothing would be lost.
  const [dismissed, setDismissed] = useState(false);

  // The note after an update: a few seconds, then gone.
  const [showUpdated, setShowUpdated] = useState(JUST_UPDATED);
  useEffect(() => {
    if (!showUpdated) return;
    const t = window.setTimeout(() => setShowUpdated(false), 3_500);
    return () => window.clearTimeout(t);
  }, [showUpdated]);

  if ((!needRefresh || dismissed) && !showUpdated) return null;
  const note = !needRefresh || dismissed;
  if (!note && !button) return null;

  // On a phone the toast sits at the top: at the bottom it covered the chat
  // composer, which is where it now shows most (a message being written is
  // exactly what holds the update).
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[120] flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] max-md:bottom-auto max-md:top-0 max-md:pb-0 max-md:pt-[calc(env(safe-area-inset-top)+8px)]">
      <div
        className="pointer-events-auto flex w-full max-w-[420px] items-center gap-3 px-4 py-3 sm:w-auto"
        style={{
          ...glassPanel,
          background: "var(--c-modal-panel)",
          borderRadius: radius.xxl,
          boxShadow: [
            `inset 0 0 0 1px ${colors.borderSubtle}`,
            `inset 0 1px 0 ${colors.bevelTopBright}`,
            "0 18px 44px rgba(0,0,0,0.5)",
          ].join(", "),
        }}
      >
        {note ? (
          <span role="status" className="flex flex-1 items-center gap-2 text-sm leading-5" style={{ color: colors.textPrimary }}>
            <Check aria-hidden className="h-4 w-4 shrink-0" strokeWidth={2} style={{ color: colors.accentLight }} />
            Updated to the latest version.
          </span>
        ) : (
          <>
            <span
              className="flex-1 text-sm leading-5"
              style={{ color: colors.textPrimary }}
            >
              {updating ? "Updating the dashboard…" : "A new version of the dashboard is available."}
            </span>
            <button
              type="button"
              onClick={apply}
              disabled={button!.disabled}
              aria-busy={updating}
              className="inline-flex h-9 shrink-0 items-center gap-2 px-4 text-sm font-medium disabled:opacity-70"
              style={{
                ...glassButtonPrimary,
                color: colors.accentLight,
                borderRadius: radius.xl,
              }}
            >
              <RefreshCw className={updating ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} strokeWidth={1.6} />
              {button!.label}
            </button>
            <Button variant="ghost" size="icon" onClick={() => setDismissed(true)} aria-label="Not now" title="Not now" className="-mr-1.5 shrink-0" disabled={updating}>
              <X aria-hidden className="h-4 w-4" strokeWidth={1.75} />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
